/**
 * SLOTS — a package's installments as ADDRESSABLE positions, not a running count.
 *
 * ## Why this exists
 *
 * api 189 picks what to bill positionally:
 *
 *     ($promotion.pattern|sort:"repeat_acc")[$$.repeat_acc > (active billing rows)]|first
 *
 * so a redemption has no identity — it is only "the (count+1)th". That is fine until
 * a line is voided, and voiding is routine. Live example, package 109263 today:
 * active rows for installments 2, 3 and 5 of 6; 1 and 4 were deleted. The count is 3,
 * so the next redemption bills the step at `repeat_acc > 3` (the 4th) and captions it
 * `4/6`; the one after captions `5/6`, which already exists. The hole is re-filled at
 * the wrong step's price and the caption collides.
 *
 * A slot fixes both, because a repeated step occupies several consecutive positions:
 *
 *     slot 1 → step 1  ค่าจัดฟันแรกเข้า 15,000   ✓ ใช้แล้ว
 *     slot 2 → step 2  ค่าปรับเครื่องมือ 1,000    ✗ ว่าง   ← default
 *     slot 3 → step 2  ค่าปรับเครื่องมือ 1,000    ✓ ใช้แล้ว
 *     slot 4 → step 2  ค่าปรับเครื่องมือ 1,000    ✗ ว่าง
 *
 * "a step can be used several times until it is full" then needs no rule of its own —
 * the slots simply run out. And slot 1 costs 15,000 while slot 4 costs 1,000, so
 * re-filling a hole charges what that position is actually worth.
 *
 * ## Where occupancy comes from
 *
 * From the ledger the drawer is already open over. `PatientTreatments` walks the
 * patient's whole history (`fetchAllTreatmentsMeta`) and Xano's addon 21
 * `billing_treatment` nests EVERY column of EVERY ACTIVE billing row per visit — so
 * `promotion_patient_id`, `billing_type_id` and `price` are in the browser before this
 * module runs. **No endpoint reads occupancy, and there is no column to store it.**
 *
 * That row set is *almost* the one api 189 counts. The difference, measured 2026-08-15:
 * the ledger's query filters `treatment.active == true`, while api 189's counter and the
 * `treatment_promotionCount` addon do NOT — so a promotion line sitting on a SOFT-DELETED
 * treatment is invisible here and still spends an installment there. That is what the
 * `count_mismatch` gate below exists for: when the rows we can see disagree with the
 * package's own `count`, nothing is addressed and the package falls back to forward-only.
 * Do not "fix" it by trusting the visible rows — the invisible one is real money.
 *
 * ## How a row is placed — by BILLING TYPE, never by the caption
 *
 * A row belongs to the pattern step whose `billing_type_id` (and, when a type appears
 * in more than one step block at different prices, whose price) it matches. Those are
 * structural fields. The obvious alternative — parsing the `n/max` that api 189
 * sprintfs into `detail` — was tried and rejected: `detail` is free text the front desk
 * edits in this very drawer, and live proof sits in the table (billing 1319623 reads
 * "Invisalign Lite 13-14", and #105's rows print `/6` where storedMax says 15).
 *
 * Which repeat *within* a step block a row occupies is not recoverable and does not
 * matter: every slot of one block bills identical money, so the ordinal is a caption,
 * not a price. Used rows therefore fill their block's earliest positions.
 *
 * ⚠️ NOTHING HERE DECIDES MONEY. A slot picks WHICH step is billed; every figure on
 * the resulting line is computed inside Xano at the moment of the write, and the server
 * re-derives capacity and refuses a step that is already full. This module can be wrong
 * and the worst outcome is a refusal.
 */
import { packageN, packageSteps, packageUsed, isPackageUsable } from './patientPromotion.js'
import { patternRows } from './promotion.js'

const int = (v) => Math.trunc(Number(v)) || 0

/* ── the pattern, as blocks ────────────────────────────────────────────────── */

// `packageSteps` (readPattern) is the ONLY correct reading of a pattern — it sorts by
// `repeat_acc` and takes each step's count from the GAP between consecutive values,
// never from the stored `repeat`, which has drifted on 23 of 224 promotions. It drops
// `repeat_acc` itself though, and the wire needs it as a checksum, so the raw rows are
// sorted the same way alongside and zipped by index.
const rawSorted = (pkg) => [...patternRows(pkg)]
  .sort((a, b) => (Number(a?.repeat_acc) || 0) - (Number(b?.repeat_acc) || 0))

function buildBlocks(pkg) {
  const raw = rawSorted(pkg)
  const steps = packageSteps(pkg)
  return steps.map((s, i) => ({
    index: i,
    // The wire address. `step_index` is the position in repeat_acc order — always
    // unique — and `step_acc` rides along so a sort disagreement between JS and
    // XanoScript becomes a refusal instead of a silently mis-addressed charge.
    step_index: i,
    step_acc: int(raw[i]?.repeat_acc),
    billing_type_id: s.billing_type_id,
    // What the line will actually cost, full stop. Until 2026-09-01 a step stored at 0
    // fell back to its billing type's own price here, mirroring what both Xano write
    // paths did — so this module, the manager's drawer and the patient's receipt each
    // named a different figure for the same step. The promotion now dictates the price,
    // 0 included, and the three agree. See knowledge/xano/promotion-price-zero.md.
    price: s.price,
    fixedDF: s.fixedDF,
    capacity: s.repeat,
  }))
}

// A pattern that can be walked at all: at least one step, strictly increasing
// repeat_acc, starting above 0. #191 (a lone step at repeat_acc 0) fails this, which is
// correct — api 189's `repeat_acc > count` can never select it, so it cannot bill.
function patternWalkable(pkg) {
  const raw = rawSorted(pkg)
  if (!raw.length) return false
  let prev = 0
  for (const r of raw) {
    const acc = Number(r?.repeat_acc)
    if (!Number.isFinite(acc) || acc <= prev) return false
    prev = acc
  }
  return true
}

// Expand the blocks into positions 1…max.
function expand(pkg, blocks) {
  const max = packageN(pkg)
  const slots = []
  if (!max || !blocks.length) return slots
  for (const b of blocks) {
    for (let k = 0; k < b.capacity && slots.length < max; k++) {
      slots.push({
        ordinal: slots.length + 1,
        blockIndex: b.index,
        step_index: b.step_index,
        step_acc: b.step_acc,
        billing_type_id: b.billing_type_id,
        price: b.price,
        state: 'free',
        billing_id: null,
      })
    }
  }
  // Only reachable when the pattern disagrees with its own max; `patternWalkable`
  // already rejects those shapes, so this is a belt on top of braces.
  const last = blocks[blocks.length - 1]
  while (slots.length < max) {
    slots.push({
      ordinal: slots.length + 1,
      blockIndex: last.index,
      step_index: last.step_index,
      step_acc: last.step_acc,
      billing_type_id: last.billing_type_id,
      price: last.price,
      state: 'free',
      billing_id: null,
    })
  }
  return slots
}

/* ── placing the rows ──────────────────────────────────────────────────────── */

/**
 * Assign each active billing row to a position, or say why it cannot be done.
 * Returns `{ ok: true }` or `{ ok: false, reason }` — and on failure the caller drops
 * the whole package to the forward-only fallback rather than addressing part of it.
 * Partial knowledge about which installments are spent is worse than none: it reads as
 * certainty on screen.
 */
function placeRows(blocks, slots, rows) {
  const byType = new Map()
  for (const b of blocks) {
    const k = Number(b.billing_type_id)
    if (!byType.has(k)) byType.set(k, [])
    byType.get(k).push(b)
  }

  for (const row of rows) {
    const group = byType.get(Number(row?.billing_type_id))
    // A type that is not in the pattern at all: the pattern was edited after this row
    // was billed (edits are retroactive — the pattern lives on the promotion, not on
    // promotion_patient), or the line was changed by hand.
    if (!group?.length) return { ok: false, reason: 'unmatched_row' }

    let target = group
    if (group.length > 1) {
      const prices = new Set(group.map((b) => b.price))
      if (prices.size > 1) {
        // The same billing type in two blocks at different prices. Price is the only
        // thing left to tell them apart; if it does not pick out exactly one, the
        // package is genuinely unknowable and must not be guessed at.
        const p = Number(row?.price)
        const hits = group.filter((b) => b.price === p)
        if (hits.length !== 1) return { ok: false, reason: 'ambiguous_type' }
        target = hits
      }
    }

    const ids = new Set(target.map((b) => b.index))
    const slot = slots.find((s) => s.state === 'free' && ids.has(s.blockIndex))
    // More rows of this step than the pattern allows — an over-redemption, or two rows
    // billed into one position by the very bug this design closes.
    if (!slot) return { ok: false, reason: 'over_capacity' }
    slot.state = 'taken'
    slot.billing_id = row?.id ?? null
  }
  return { ok: true }
}

/* ── the map ───────────────────────────────────────────────────────────────── */

/**
 * The full picture for one package.
 *
 * @param pkg   a promotion_patient row from `GET /api/promotion-patient`
 * @param rows  this package's ACTIVE billing rows (already filtered by
 *              promotion_patient_id), or null when the caller has none
 * @param opts  {complete} — whether the ledger walk covered the whole history
 *
 * `known: false` is the forward-only world: the first `count` positions are treated as
 * spent, exactly as api 189 sees them, and only the run from the next one forward can
 * be billed. That is today's live behaviour, kept as the fallback so there is ONE code
 * path and every failure degrades to "still correct, just hole-blind".
 */
export function buildSlotMap(pkg, rows, { complete = true } = {}) {
  const blocks = buildBlocks(pkg)
  const slots = expand(pkg, blocks)
  const usable = isPackageUsable(pkg)

  const shell = (known, reason) => {
    const free = slots.filter((s) => s.state === 'free').map((s) => s.ordinal)
    return {
      max: slots.length,
      slots,
      blocks,
      free,
      defaultPick: usable && free.length ? free[0] : null,
      known,
      reason,
      usable,
    }
  }

  if (!slots.length) return shell(false, 'no_pattern')

  // Every reason the derivation cannot be trusted. Each one keeps the package usable —
  // it just stops being addressable.
  const forwardOnly = (reason) => {
    for (const s of slots) { s.state = 'free'; s.billing_id = null }
    const used = Math.min(packageUsed(pkg), slots.length)
    for (let i = 0; i < used; i++) slots[i].state = 'taken'
    return shell(false, reason)
  }

  if (!Array.isArray(rows)) return forwardOnly('no_rows')
  if (!complete) return forwardOnly('truncated')
  if (!patternWalkable(pkg)) return forwardOnly('bad_pattern')
  // The ledger and the `treatment_promotionCount` addon count the same thing. If they
  // disagree, a row is missing from one of them and every "free" position below is
  // suspect — including, dangerously, one that is actually billed.
  if (rows.length !== packageUsed(pkg)) return forwardOnly('count_mismatch')

  const placed = placeRows(blocks, slots, rows)
  if (!placed.ok) return forwardOnly(placed.reason)

  return shell(true, null)
}

/* ── selection ─────────────────────────────────────────────────────────────── */

// Ticking a slot. Free slots only, always returned in charging order — the write loop
// redeems them in this order and a receipt printed out of order reads as a different plan.
export function toggleSlot(selection, ordinal, map) {
  const list = Array.isArray(selection) ? selection : []
  if (list.includes(ordinal)) return list.filter((o) => o !== ordinal)
  if (!map?.free?.includes(ordinal)) return list
  return [...list, ordinal].sort((a, b) => a - b)
}

// The chosen positions, as slot objects. What the confirm dialog states before anything
// is written, and where the wire addresses come from.
export function selectionRows(map, selection) {
  const byOrdinal = new Map((map?.slots || []).map((s) => [s.ordinal, s]))
  return (selection || []).map((o) => byOrdinal.get(o)).filter(Boolean)
}

/**
 * What goes on the wire. ADDRESSED packages name their step; forward-only ones can
 * only say how many, because api 189 chooses for itself and any number we sent would
 * be decoration over a different decision.
 */
export function redeemPayload(map, selection) {
  const rows = selectionRows(map, selection)
  if (!rows.length) return null
  if (!map.known) return { repeat: rows.length }
  // `ordinal` is DISPLAY ONLY and the server ignores it — Xano computes the real
  // caption from the pattern. It rides along so a refusal can name the installment
  // the user actually clicked instead of an internal index.
  return {
    steps: rows.map((s) => ({ step_index: s.step_index, step_acc: s.step_acc, ordinal: s.ordinal })),
  }
}

// What a position will cost, preview only. NEGATIVE IS ROUTINE (the coupon promotions
// store −200/−300/−500) and must read as a discount; 0 is FREE, and stepPriceLabel
// renders it as ฟรี.
export const slotPrice = (s) => Number(s?.price) || 0

// Not "how many did you tick" — how many are left after them, which is the number the
// front desk is asked about at the chair.
export const remainingAfter = (map, selection) =>
  Math.max(0, (map?.free?.length || 0) - (selection?.length || 0))

/* ── occupancy plumbing ────────────────────────────────────────────────────── */

// Split the ledger's promotion rows by package. The caller hands over every active
// billing row it holds; this is the only place that knows the key.
export function rowsByPackage(rows) {
  const out = new Map()
  for (const b of Array.isArray(rows) ? rows : []) {
    const k = int(b?.promotion_patient_id)
    if (!k) continue
    if (!out.has(k)) out.set(k, [])
    out.get(k).push(b)
  }
  return out
}
